/**
 * 权限管理工具
 */

// 权限树节点类型
export interface PermissionTreeNode {
  title: string;
  key: string;
  route?: string;
  children?: PermissionTreeNode[];
}

/** 是否超级管理员通配（["*"] / "*"） */
const isSuperAdminModules = (parsed: unknown): boolean => {
  if (parsed === '*') return true;
  if (Array.isArray(parsed) && parsed.length === 1 && parsed[0] === '*') return true;
  return false;
};

/** 从权限树生成全量权限 map（超级管理员用） */
const buildAllPermissions = (): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  const walk = (nodes: PermissionTreeNode[]) => {
    for (const node of nodes) {
      if (node.route && node.children?.length) {
        result[node.key] = node.children
          .map((c) => c.key.split('.').pop()!)
          .filter(Boolean);
      }
      if (node.children?.length) walk(node.children);
    }
  };
  walk(treeData);
  return result;
};

// 解析权限模块字符串
export const parsePermissions = (modulesStr: string): Record<string, string[]> => {
  try {
    const parsed = JSON.parse(modulesStr);
    if (isSuperAdminModules(parsed)) {
      return buildAllPermissions();
    }
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, string[]>;
    }
    return {};
  } catch {
    return {};
  }
};

// 检查是否有指定权限
export const hasPermission = (userPermissions: Record<string, string[]>, permission: string): boolean => {
  const parts = permission.split('.');
  if (parts.length === 2) {
    // 兼容旧格式：module.action
    const [module, action] = parts;
    const modulePermissions = userPermissions[module];
    return modulePermissions?.includes(action) || modulePermissions?.includes('*') || false;
  } else if (parts.length === 3) {
    // 新格式：module.page.action
    const moduleKey = `${parts[0]}.${parts[1]}`;
    const action = parts[2];
    const modulePermissions = userPermissions[moduleKey];
    return modulePermissions?.includes(action) || modulePermissions?.includes('*') || false;
  }
  return false;
};

// 检查是否有任一权限
export const hasAnyPermission = (userPermissions: Record<string, string[]>, permissions: string[]): boolean => {
  return permissions.some(permission => hasPermission(userPermissions, permission));
};

// 获取用户所有权限
export const getAllPermissions = (userPermissions: Record<string, string[]>): string[] => {
  const permissions: string[] = [];
  Object.entries(userPermissions).forEach(([moduleKey, actions]) => {
    actions.forEach(action => {
      permissions.push(`${moduleKey}.${action}`);
    });
  });
  return permissions;
};

// 递归查找第一个可访问的路由
const findFirstRouteRecursive = (
  nodes: PermissionTreeNode[],
  userPermissions: Record<string, string[]>,
): string | null => {
  for (const node of nodes) {
    // 节点如果有关联的路由，则检查视图权限
    // 按照约定，页面的查看权限是 "模块.页面.view"
    if (node.route) {
      const viewPermission = `${node.key}.view`;
      if (hasPermission(userPermissions, viewPermission)) {
        return node.route; // 找到第一个可访问的路由
      }
    }

    // 如果当前节点有子节点，则递归查找
    if (node.children && node.children.length > 0) {
      const accessibleChildRoute = findFirstRouteRecursive(node.children, userPermissions);
      if (accessibleChildRoute) {
        return accessibleChildRoute; // 从子节点返回找到的路由
      }
    }
  }

  return null; // 在此分支未找到可访问的路由
};

/**
 * 根据用户的权限模块和权限树，获取用户可以访问的第一个路由。
 * @param modulesStr - 从后端获取的用户权限模块JSON字符串。
 * @returns 第一个可访问的路由路径，如果找不到则返回 null。
 */
export const getFirstAccessibleRoute = (modulesStr: string): string | null => {
  const userPermissions = parsePermissions(modulesStr);
  return findFirstRouteRecursive(treeData, userPermissions);
};

// 权限树数据（基于路由结构）
export const treeData: PermissionTreeNode[] = [
  {
    title: '鉴定管理',
    key: 'appraisal',
    children: [
      {
        title: '鉴定分类',
        key: 'appraisal.category',
        route: '/appraisal/category',
        children: [
          { title: '查看', key: 'appraisal.category.view' },
          { title: '新增', key: 'appraisal.category.add' },
          { title: '编辑', key: 'appraisal.category.edit' },
          { title: '删除', key: 'appraisal.category.delete' },
        ],
      },
      {
        title: '鉴定类目',
        key: 'appraisal.identity',
        route: '/appraisal/identity',
        children: [
          { title: '查看', key: 'appraisal.identity.view' },
          { title: '新增', key: 'appraisal.identity.add' },
          { title: '编辑', key: 'appraisal.identity.edit' },
          { title: '删除', key: 'appraisal.identity.delete' },
        ],
      },
      {
        title: '鉴定历史',
        key: 'appraisal.identityHistory',
        route: '/appraisal/identityHistory',
        children: [
          { title: '查看', key: 'appraisal.identityHistory.view' },
          { title: '开始鉴定', key: 'appraisal.identityHistory.start' },
        ],
      },
      {
        title: "引导页",
        key: "appraisal.guide",
        route: "/appraisal/guide",
        children: [
          { title: "查看", key: "appraisal.guide.view" },
          { title: "新增", key: "appraisal.guide.add" },
          { title: "编辑", key: "appraisal.guide.edit" },
          { title: "删除", key: "appraisal.guide.delete" },
        ],
      },
      {
        title: "预约价格1",
        key: "appraisal.reservation",
        route: "/appraisal/reservation",
        children: [
          { title: "查看", key: "appraisal.reservation.view" },
          { title: "编辑", key: "appraisal.reservation.edit" },
        ],
      },
      {
        title: "品牌预约",
        key: "appraisal.brand",
        route: "/appraisal/brand",
        children: [
          { title: "查看", key: "appraisal.brand.view" },
          { title: "新增", key: "appraisal.brand.add" },
          { title: "编辑", key: "appraisal.brand.edit" },
          { title: "删除", key: "appraisal.brand.delete" }
        ],
      }
    ],
  },
  {
    title: '用户管理',
    key: 'userManager',
    children: [
      {
        title: '用户列表',
        key: 'userManager.list',
        route: '/userManager/list',
        children: [
          { title: '查看', key: 'userManager.list.view' },
          { title: '新增', key: 'userManager.list.add' },
          { title: '编辑', key: 'userManager.list.edit' },
          { title: '删除', key: 'userManager.list.delete' },
        ],
      },
      {
        title: '交易明细',
        key: 'userManager.transaction',
        route: '/userManager/transaction',
        children: [
          { title: '查看', key: 'userManager.transaction.view' },
          { title: '新增', key: 'userManager.transaction.add' },
          { title: '编辑', key: 'userManager.transaction.edit' },
          { title: '删除', key: 'userManager.transaction.delete' },
        ],
      },
      {
        title: '动态管理',
        key: 'userManager.appoint',
        route: '/userManager/appoint',
        children: [
          { title: '查看', key: 'userManager.appoint.view' },
          { title: '审核', key: 'userManager.appoint.audit' },
        ],
      },
      {
        title: '实名认证',
        key: 'userManager.auth',
        route: '/userManager/auth',
        children: [
          { title: '查看', key: 'userManager.auth.view' },
          { title: '审核', key: 'userManager.auth.audit' },
        ],
      },
      {
        title: '地址管理',
        key: 'userManager.address',
        route: '/userManager/address',
        children: [
          { title: '查看', key: 'userManager.address.view' },
          // { title: '新增', key: 'userManager.address.add' },
          // { title: '编辑', key: 'userManager.address.edit' },
          // { title: '删除', key: 'userManager.address.delete' },
        ],
      },
      {
        title: '评价管理',
        key: 'userManager.comment',
        route: '/userManager/comment',
        children: [
          { title: '查看', key: 'userManager.comment.view' },
          // { title: '新增', key: 'userManager.address.add' },
          // { title: '编辑', key: 'userManager.address.edit' },
          // { title: '删除', key: 'userManager.address.delete' },
        ],
      },
      {
        title: '提现管理',
        key: 'userManager.withdraw',
        route: '/userManager/withdraw',
        children: [
          { title: '查看', key: 'userManager.withdraw.view' },
          { title: '审核', key: 'userManager.withdraw.audit' },
          // { title: '编辑', key: 'userManager.address.edit' },
          // { title: '删除', key: 'userManager.address.delete' },
        ],
      },
      {
        title: '鉴定师列表',
        key: 'userManager.appraiser',
        route: '/userManager/appraiser',
        children: [
          { title: '查看', key: 'userManager.appraiser.view' },
          { title: '编辑', key: 'userManager.appraiser.edit' },
        ],
      },
    ],
  },
  {
    title: '社区管理',
    key: 'community',
    children: [
      {
        title: '话题管理',
        key: 'community.topic',
        route: '/community/topic',
        children: [
          { title: '查看', key: 'community.topic.view' },
          { title: '新增', key: 'community.topic.add' },
          { title: '编辑', key: 'community.topic.edit' },
          { title: '删除', key: 'community.topic.delete' },
        ],
      },
    ],
  },
  {
    title: '商城管理',
    key: 'mall',
    children: [
      {
        title: '商品管理',
        key: 'mall.product',
        route: '/mall/product',
        children: [
          { title: '查看', key: 'mall.product.view' },
          { title: '新增', key: 'mall.product.add' },
          { title: '编辑', key: 'mall.product.edit' },
          { title: '删除', key: 'mall.product.delete' },
          { title: '审核', key: 'mall.product.audit' },
        ],
      },
      {
        title: '商品分类',
        key: 'mall.category',
        route: '/mall/category',
        children: [
          { title: '查看', key: 'mall.category.view' },
          { title: '新增', key: 'mall.category.add' },
          { title: '编辑', key: 'mall.category.edit' },
          { title: '删除', key: 'mall.category.delete' },
        ],
      },
      {
        title: '订单管理',
        key: 'mall.order',
        route: '/mall/order',
        children: [
          { title: '查看', key: 'mall.order.view' },
          { title: '新增', key: 'mall.order.add' },
          { title: '编辑', key: 'mall.order.edit' },
          { title: '删除', key: 'mall.order.delete' },
        ],
      },
      {
        title: '订单评价',
        key: 'mall.orderComment',
        route: '/mall/orderComment',
        children: [
          { title: '查看', key: 'mall.orderComment.view' }
        ],
      },
      {
        title: '优惠券管理',
        key: 'mall.coupon',
        route: '/mall/coupon',
        children: [
          { title: '查看', key: 'mall.coupon.view' },
          { title: '新增', key: 'mall.coupon.add' },
        ],
      },
      {
        title: '优惠券领取记录',
        key: 'mall.couponRedemption',
        route: '/mall/couponRedemption',
        children: [
          { title: '查看', key: 'mall.couponRedemption.view' },
        ],
      },
      {
        title: '优惠券使用记录',
        key: 'mall.couponUse',
        route: '/mall/couponUse',
        children: [
          { title: '查看', key: 'mall.couponUse.view' },
        ],
      },
    ],
  },
  {
    title: '系统管理',
    key: 'system',
    children: [
      {
        title: '管理员管理',
        key: 'system.manager',
        route: '/system/manager',
        children: [
          { title: '查看', key: 'system.manager.view' },
          { title: '新增', key: 'system.manager.add' },
          { title: '编辑', key: 'system.manager.edit' },
          { title: '删除', key: 'system.manager.delete' },
        ],
      },
      {
        title: '角色管理',
        key: 'system.role',
        route: '/system/role',
        children: [
          { title: '查看', key: 'system.role.view' },
          { title: '新增', key: 'system.role.add' },
          { title: '编辑', key: 'system.role.edit' },
          { title: '删除', key: 'system.role.delete' },
        ],
      },
      {
        title: '权限日志',
        key: 'system.permissionLog',
        route: '/system/permissionLog',
        children: [
          { title: '查看', key: 'system.permissionLog.view' },
        ],
      },
    ],
  },
];